import React, { useState, useEffect } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import api from "../../services/api";

const AllocationForm = ({ formData, onFormChange, onAllAssignedChange }) => {
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [allClassStreams, setAllClassStreams] = useState([]);
  const [streams, setStreams] = useState([]);
  const [terms, setTerms] = useState([]);
  const [existingAllocations, setExistingAllocations] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [teachersRes, classesRes, termsRes, csRes] = await Promise.all([
          api.get('/report-card/teachers/active'),
          api.get('/academic/classes'),
          api.get('/academic/terms'),
          api.get('/academic/class-streams-filter'),
        ]);
        setTeachers(Array.isArray(teachersRes.data) ? teachersRes.data : []);
        setClasses(Array.isArray(classesRes.data?.data) ? classesRes.data.data : Array.isArray(classesRes.data) ? classesRes.data : []);
        setTerms(Array.isArray(termsRes.data?.data) ? termsRes.data.data : Array.isArray(termsRes.data) ? termsRes.data : []);
        setAllClassStreams(Array.isArray(csRes.data) ? csRes.data : []);
      } catch (error) {}
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!formData.academic_year) { setExistingAllocations([]); return; }
    const params = { academic_year: formData.academic_year, is_active: 1, per_page: 500 };
    if (formData.term_id) params.term_id = formData.term_id;
    api.get('/report-card/class-teacher-allocations', { params })
      .then(res => setExistingAllocations(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => {});
  }, [formData.academic_year, formData.term_id]);

  useEffect(() => {
    if (formData.class_id) {
      const forClass = allClassStreams.filter(cs => cs.class_id === parseInt(formData.class_id));
      setStreams(forClass);
      if (forClass.length === 1 && !isStreamAssigned(parseInt(formData.class_id), forClass[0].stream_id)) {
        onFormChange({ stream_id: forClass[0].stream_id });
      }
    } else {
      setStreams([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.class_id, allClassStreams]);

  const isStreamAssigned = (classId, streamId) =>
    existingAllocations.some(a => a.class_id === classId && (streamId ? a.stream_id === streamId : !a.stream_id));

  const allAssigned = classes.length > 0 && classes.every(cls => {
    const clsStreams = allClassStreams.filter(cs => cs.class_id === cls.id);
    if (clsStreams.length === 0) return isStreamAssigned(cls.id, null);
    return clsStreams.every(cs => isStreamAssigned(cls.id, cs.stream_id));
  });

  useEffect(() => { onAllAssignedChange?.(allAssigned); }, [allAssigned, onAllAssignedChange]);

  const availableClasses = classes.filter(cls => {
    const clsStreams = allClassStreams.filter(cs => cs.class_id === cls.id);
    if (clsStreams.length === 0) return !isStreamAssigned(cls.id, null);
    return clsStreams.some(cs => !isStreamAssigned(cls.id, cs.stream_id));
  });

  const availableStreams = streams.filter(s => !isStreamAssigned(parseInt(formData.class_id), s.stream_id));

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: 'black' }}>
        Allocation Details
      </Typography>
      {allAssigned && (
        <Alert severity="info" sx={{ mb: 2 }}>
          All classes and streams already have a class teacher assigned for this year/term.
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <Autocomplete
            fullWidth
            options={teachers}
            getOptionLabel={(t) => `${t.full_name || `${t.last_name} ${t.first_name}`} (${t.staff_number})`}
            value={teachers.find(t => t.id === formData.teacher_id) || null}
            onChange={(_, val) => onFormChange({ teacher_id: val?.id || '' })}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderInput={(params) => <TextField {...params} label="Teacher" required />}
          />
        </Grid>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <Autocomplete
            fullWidth
            options={availableClasses}
            getOptionLabel={(c) => c.name || c.class_name || ''}
            value={classes.find(c => c.id === formData.class_id) || null}
            onChange={(_, val) => onFormChange({ class_id: val?.id || '', stream_id: '' })}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderInput={(params) => <TextField {...params} label="Class" required />}
          />
        </Grid>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <Autocomplete
            fullWidth
            options={availableStreams}
            getOptionLabel={(s) => s.stream_name || s.name || ''}
            value={streams.find(s => s.stream_id === formData.stream_id) || null}
            onChange={(_, val) => onFormChange({ stream_id: val?.stream_id || '' })}
            isOptionEqualToValue={(opt, val) => opt.stream_id === val.stream_id}
            disabled={!formData.class_id}
            renderInput={(params) => <TextField {...params} label="Stream" />}
          />
        </Grid>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <TextField
            fullWidth
            type="number"
            label="Academic Year"
            value={formData.academic_year}
            onChange={(e) => onFormChange({ academic_year: e.target.value })}
            inputProps={{ min: 2020, max: new Date().getFullYear() }}
            required
          />
        </Grid>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <Autocomplete
            fullWidth
            options={terms}
            getOptionLabel={(t) => t.term_name || t.name || ''}
            value={terms.find(t => t.id === formData.term_id) || null}
            onChange={(_, val) => {
              onFormChange({ term_id: val?.id || '', ...(val ? { start_date: val.start_date || '', end_date: val.end_date || '' } : {}) });
            }}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderInput={(params) => <TextField {...params} label="Term" />}
          />
        </Grid>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={formData.start_date ? dayjs(formData.start_date) : null}
              onChange={(v) => onFormChange({ start_date: v ? v.format('YYYY-MM-DD') : '' })}
              slotProps={{ textField: { fullWidth: true, required: true } }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="End Date"
              value={formData.end_date ? dayjs(formData.end_date) : null}
              onChange={(v) => onFormChange({ end_date: v ? v.format('YYYY-MM-DD') : '' })}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <FormControlLabel
            control={<Switch checked={formData.is_primary} onChange={(e) => onFormChange({ is_primary: e.target.checked })} />}
            label="Primary Class Teacher"
          />
        </Grid>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <FormControlLabel
            control={<Switch checked={formData.is_active} onChange={(e) => onFormChange({ is_active: e.target.checked })} />}
            label="Active"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AllocationForm;
